import axios, { AxiosInstance } from 'axios';
import { apiConfig } from '../config/authConfig';
import {
  ApiResponse,
  User,
  Question,
  Vote,
  AnalyticsData,
  QuestionWithVoters,
  QuestionStatus,
} from '../types';

const createApiClient = (token?: string): AxiosInstance => {
  const client = axios.create({
    baseURL: apiConfig.baseUrl,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  return client;
};

// User service
export const userService = {
  getCurrentUser: async (token: string): Promise<ApiResponse<User>> => {
    const client = createApiClient(token);
    const response = await client.get<ApiResponse<User>>('/user/me');
    return response.data;
  },
};

// Questions service
export const questionsService = {
  getAll: async (token: string): Promise<ApiResponse<Question[]>> => {
    const client = createApiClient(token);
    const response = await client.get<ApiResponse<Question[]>>('/questions');
    return response.data;
  },

  getById: async (token: string, id: number): Promise<ApiResponse<Question>> => {
    const client = createApiClient(token);
    const response = await client.get<ApiResponse<Question>>(`/questions/${id}`);
    return response.data;
  },

  create: async (token: string, questionText: string, isAnonymous: boolean = false): Promise<ApiResponse<Question>> => {
    const client = createApiClient(token);
    const response = await client.post<ApiResponse<Question>>('/questions', { questionText, isAnonymous });
    return response.data;
  },

  update: async (
    token: string,
    id: number,
    data: { questionText?: string; status?: QuestionStatus; isHidden?: boolean }
  ): Promise<ApiResponse> => {
    const client = createApiClient(token);
    const response = await client.put<ApiResponse>(`/questions/${id}`, data);
    return response.data;
  },

  delete: async (token: string, id: number): Promise<ApiResponse> => {
    const client = createApiClient(token);
    const response = await client.delete<ApiResponse>(`/questions/${id}`);
    return response.data;
  },
};

// Votes service
export const votesService = {
  addVote: async (token: string, questionId: number): Promise<ApiResponse<Vote>> => {
    const client = createApiClient(token);
    const response = await client.post<ApiResponse<Vote>>(`/votes/${questionId}`);
    return response.data;
  },

  removeVote: async (token: string, questionId: number): Promise<ApiResponse> => {
    const client = createApiClient(token);
    const response = await client.delete<ApiResponse>(`/votes/${questionId}`);
    return response.data;
  },

  checkVoteStatus: async (
    token: string,
    questionId: number
  ): Promise<ApiResponse<{ hasVoted: boolean }>> => {
    const client = createApiClient(token);
    const response = await client.get<ApiResponse<{ hasVoted: boolean }>>(
      `/votes/${questionId}/status`
    );
    return response.data;
  },
};

// Admin service
export const adminService = {
  getAllQuestions: async (token: string): Promise<ApiResponse<Question[]>> => {
    const client = createApiClient(token);
    const response = await client.get<ApiResponse<Question[]>>('/admin/questions');
    return response.data;
  },

  getQuestionsWithVoters: async (token: string): Promise<ApiResponse<QuestionWithVoters[]>> => {
    const client = createApiClient(token);
    const response = await client.get<ApiResponse<QuestionWithVoters[]>>(
      '/admin/questions/with-voters'
    );
    return response.data;
  },

  updateVisibility: async (
    token: string,
    questionId: number,
    isHidden: boolean
  ): Promise<ApiResponse> => {
    const client = createApiClient(token);
    const response = await client.patch<ApiResponse>(
      `/admin/questions/${questionId}/visibility`,
      { isHidden }
    );
    return response.data;
  },

  updateStatus: async (
    token: string,
    questionId: number,
    status: QuestionStatus
  ): Promise<ApiResponse> => {
    const client = createApiClient(token);
    const response = await client.patch<ApiResponse>(`/admin/questions/${questionId}/status`, {
      status,
    });
    return response.data;
  },

  getAnalytics: async (token: string): Promise<ApiResponse<AnalyticsData>> => {
    const client = createApiClient(token);
    const response = await client.get<ApiResponse<AnalyticsData>>('/admin/analytics');
    return response.data;
  },

  exportToExcel: async (token: string): Promise<Blob> => {
    const client = createApiClient(token);
    const response = await client.get('/admin/export', {
      responseType: 'blob',
    });
    return response.data;
  },
};
